/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable compat/compat */
/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable complexity */
import { collection, getDocs } from "firebase/firestore";
import { firestore } from "@/base";
import { CEM, DEZ, DOIS, QUATRO } from "@/utils/constants";

interface ProcessarPlanilhaParameters {
  licenciadaId: string;
  empresaId: string;
  empresaDesejadaId?: string;
  questionarioId?: string;
}

interface EmpresaData {
  empresaId: string;
  empresaNome: string;
  empresaRazaoSocial: string;
}

interface TotalizadorGrupo {
  grupoId: string;
  grupoNome: string | null;
  grupoPeso: number | null;
  notaAA: number;
  atingimentoAA: string;
  notaSegundaEntrevista: number | null;
  somaNotaSegundaEntrevista: number;
  atingimentoSegundaEntrevista: string | null;
  somaPesoGrupos: number;
  fatorCorrecao: string;
  fatorCorrecaoAplicado: number;
  somaFatorCorrecaoAplicado: number;
  notaCorrigida: number;
  somaNotaCorrigida: number;
  correcaoAplicada: string;
}

interface ConferenciaResult {
  empresa: EmpresaData | null;
  totalRespostas: number;
  totalPerguntas: number;
  totalGrupos: number;
  totalMedias: number;
  mediaAtual: number;
  mediaAtualFonte: string;
  mediaAA: string;
  mediaAvaliadores: string;
  totalizadoresPorGrupo: TotalizadorGrupo[];
  respostas: any[];
  dadosMedias?: any[];
  dadosRespostas?: any[];
}

const roundTo = (value: number, decimals: number): number => {
  const factor = Math.pow(DEZ, decimals);
  return Math.round(value * factor) / factor;
};

const fetchFirestoreData = async (parameters: ProcessarPlanilhaParameters) => {
  const { licenciadaId, empresaId, empresaDesejadaId, questionarioId } = parameters;
  const [snapPerguntas, snapGrupos, snapEmpresas, snapRespostas, snapMedias] = await Promise.all([
    getDocs(collection(firestore, `licenciadas/${licenciadaId}/empresas/${empresaId}/perguntas`)),
    getDocs(collection(firestore, `licenciadas/${licenciadaId}/empresas/${empresaId}/gruposDiagnostico`)),
    getDocs(collection(firestore, `licenciadas/${licenciadaId}/empresas`)),
    getDocs(collection(firestore, `licenciadas/${licenciadaId}/empresas/${empresaId}/respostas`)),
    getDocs(collection(firestore, `licenciadas/${licenciadaId}/empresas/${empresaId}/medias`))
  ]);

  const perguntas = snapPerguntas.docs.map((document_) => ({ id: document_.id, ...document_.data() }));
  let grupos = snapGrupos.docs.map((document_) => ({ id: document_.id, ...document_.data() }));
  const empresas = snapEmpresas.docs.map((document_) => ({ id: document_.id, ...document_.data() }));
  let respostas = snapRespostas.docs.map((document_) => ({ id: document_.id, ...document_.data() }));
  let medias = snapMedias.docs.map((document_) => ({ id: document_.id, ...document_.data() }));

  if (questionarioId) {
    grupos = grupos.filter((g: any) => g.idQuestionario === questionarioId);
    respostas = respostas.filter((r: any) => r.idQuestionario === questionarioId);
  }

  if (empresaDesejadaId) {
    respostas = respostas.filter((r: any) => r.idEmpresa === empresaDesejadaId);
    medias = medias.filter((m: any) => m.idEmpresaNota === empresaDesejadaId);
  }

  const gruposIds = new Set(grupos.map((g: any) => g.id));
  medias = medias.filter((m: any) => gruposIds.has(m.grupo));

  return { perguntas, grupos, empresas, respostas, medias };
};

// Enriquecer respostas com dados de perguntas e grupos
const enriquecerRespostas = (respostas: any[], perguntas: any[], grupos: any[]) => {
  return respostas.map((resposta: any) => {
    const perguntasEnriquecidas = resposta.perguntas?.map((perguntaResposta: any) => {
      const pergunta = perguntas.find((p: any) => p.id === perguntaResposta.id);
      const grupo = grupos.find((g: any) => g.id === perguntaResposta.grupo);

      return {
        ...perguntaResposta,
        perguntaDescricao: pergunta?.descricao || null,
        perguntaPeso: pergunta?.peso || null,
        grupoNome: grupo?.nome || null,
        grupoOrdem: grupo?.ordem || null
      };
    });

    return { ...resposta, perguntas: perguntasEnriquecidas };
  });
};

const calcularTotalizadores = (
  respostasEnriquecidas: any[],
  grupos: any[],
  tipoCalculo: "respostas" | "medias" = "respostas"
) => {
  const dados = tipoCalculo === "respostas" ? respostasEnriquecidas : [];
  const isMedias = tipoCalculo === "medias";
  const respondentesUnicos = new Set();
  dados.forEach((item: any) => {
    const respondente = isMedias ? item.respondente : item.idRespondente;
    if (respondente) respondentesUnicos.add(respondente);
  });

  const quantidadePessoas = respondentesUnicos.size;

  const somaPorGrupo: any = {};

  dados.forEach((item: any) => {
    const perguntas = isMedias ? [item] : item.perguntas || [];

    perguntas.forEach((perguntaResposta: any) => {
      const grupoId = perguntaResposta.grupo;
      const valor = Number.parseFloat(isMedias ? perguntaResposta.nota : perguntaResposta.resposta?.valor);
      const peso = Number.parseFloat(perguntaResposta.perguntaPeso) || 0;

      if (!Number.isNaN(valor) && grupoId) {
        if (!somaPorGrupo[grupoId]) {
          const grupoData = grupos.find((g: any) => g.id === grupoId);
          somaPorGrupo[grupoId] = {
            grupoId,
            grupoNome: grupoData?.nome || null,
            grupoPeso: grupoData?.pesoNota || null,
            soma: 0,
            somaPonderada: 0,
            quantidade: 0
          };
        }

        somaPorGrupo[grupoId].soma += valor;
        somaPorGrupo[grupoId].somaPonderada += (valor / DEZ) * peso;
        somaPorGrupo[grupoId].quantidade += 1;
      }
    });
  });

  let somaPercentuaisAtingimento = 0;
  let quantidadeGruposValidos = 0;

  Object.values(somaPorGrupo).forEach((grupo: any) => {
    const percentualAtingimento =
      grupo.grupoPeso > 0 ? roundTo((grupo.somaPonderada / quantidadePessoas) * (CEM / grupo.grupoPeso), DOIS) : 0;

    if (grupo.grupoPeso > 0) {
      somaPercentuaisAtingimento += percentualAtingimento;
      quantidadeGruposValidos++;
    }
  });

  const mediaPercentuaisAtingimento =
    quantidadeGruposValidos > 0 ? roundTo(somaPercentuaisAtingimento / quantidadeGruposValidos / DEZ, DOIS) : 0;

  const totalizadores = Object.values(somaPorGrupo).map((grupo: any) => {
    const notaAA = quantidadePessoas > 0 ? grupo.somaPonderada / quantidadePessoas / DEZ : 0;
    const atingimentoAA = grupo.grupoPeso > 0 ? (notaAA / grupo.grupoPeso) * CEM : 0;

    return {
      grupoId: grupo.grupoId,
      grupoNome: grupo.grupoNome,
      grupoPeso: grupo.grupoPeso,
      notaAA: roundTo(notaAA, QUATRO),
      atingimentoAA: `${roundTo(atingimentoAA, DOIS)}%`
    };
  });

  return { totalizadores, mediaPercentuaisAtingimento };
};

const gerarLogCSV = (respostasEnriquecidas: any[], medias: any[], empresas: any[], empresaDesejadaId?: string) => {
  let nomeEmpresa = "SEM_EMPRESA";
  if (empresaDesejadaId) {
    const empresa = empresas.find((e: any) => e.id === empresaDesejadaId);
    nomeEmpresa = empresa?.nomeFantasia || empresa?.razaoSocial || empresaDesejadaId;
  }
  const dadosPorPergunta: any = {};
  respostasEnriquecidas.forEach((resposta: any) => {
    resposta.perguntas?.forEach((perguntaResposta: any) => {
      const perguntaId = perguntaResposta.id;
      const valor = Number.parseFloat(perguntaResposta.resposta?.valor);

      if (!Number.isNaN(valor)) {
        if (!dadosPorPergunta[perguntaId]) {
          dadosPorPergunta[perguntaId] = {
            grupoNome: perguntaResposta.grupoNome || "SEM_GRUPO",
            perguntaDescricao: (perguntaResposta.perguntaDescricao || "SEM_DESCRICAO").slice(0, CEM),
            valores: []
          };
        }
        dadosPorPergunta[perguntaId].valores.push(valor);
      }
    });
  });

  const csvLines: string[] = [
    '"nomeempresa","idrespondente","nome grupo","nome pergunta","mediaRespostas","mediaMedias"'
  ];

  for (const resposta of respostasEnriquecidas) {
    for (const perguntaResposta of resposta.perguntas || []) {
      const perguntaId = perguntaResposta.id;
      const valor = Number.parseFloat(perguntaResposta.resposta?.valor);
      const idRespondente = resposta.idRespondente || "SEM_ID";

      if (!Number.isNaN(valor)) {
        // Calcular médias
        const dados = dadosPorPergunta[perguntaId];
        const mediaRespostas =
          dados?.valores.length > 0
            ? roundTo(
                dados.valores.reduce((accumulator: number, value: number) => accumulator + value, 0) /
                  dados.valores.length,
                DOIS
              ).toFixed(DOIS)
            : "0.00";

        const mediaCorrespondente = medias.find((m: any) => m.idResposta === perguntaId);
        const mediaMedias = mediaCorrespondente
          ? roundTo(Number.parseFloat(mediaCorrespondente.nota), DOIS).toFixed(DOIS)
          : "0.00";

        const descricaoTruncada = (perguntaResposta.perguntaDescricao || "SEM_DESCRICAO").slice(0, CEM);
        csvLines.push(
          `"${nomeEmpresa}","${idRespondente}","${perguntaResposta.grupoNome || "SEM_GRUPO"}","${descricaoTruncada}","${mediaRespostas}","${mediaMedias}"`
        );
      }
    }
  }
};

const calcularValoresFinais = (
  totalizadoresRespostas: any[],
  totalizadoresMedias: any[],
  medias: any[],
  respostasEnriquecidas: any[],
  mediaAtingimentoRespostas: number
) => {
  const totalizadoresMesclados = totalizadoresRespostas.map((grupo: any) => {
    const grupoMedia = totalizadoresMedias.find((gm: any) => gm.grupoId === grupo.grupoId);
    return {
      ...grupo,
      notaSegundaEntrevista: grupoMedia?.notaAA || null,
      atingimentoSegundaEntrevista: grupoMedia?.atingimentoAA || null
    };
  });

  const somaNotaSegundaEntrevista = totalizadoresMesclados.reduce(
    (accumulator: number, grupo: any) => accumulator + (grupo.notaSegundaEntrevista || 0),
    0
  );

  const somaPesoGrupos = totalizadoresMesclados.reduce(
    (accumulator: number, grupo: any) => accumulator + (grupo.grupoPeso || 0),
    0
  );

  const fatorCorrecao = somaPesoGrupos > 0 ? (somaNotaSegundaEntrevista / somaPesoGrupos) * CEM : 0;

  const totalizadoresComNotasCorrigidas = totalizadoresMesclados.map((grupo: any) => {
    const fatorCorrecaoAplicado = (grupo.grupoPeso || 0) * (fatorCorrecao / CEM);
    const atingimentoNumerico = grupo.atingimentoSegundaEntrevista
      ? Number.parseFloat(grupo.atingimentoSegundaEntrevista.replace("%", ""))
      : 0;

    const notaCorrigida = Math.round(fatorCorrecaoAplicado) * (atingimentoNumerico / CEM);
    const correcaoAplicada = (grupo.grupoPeso || 0) > 0 ? (notaCorrigida / grupo.grupoPeso) * CEM : 0;

    return {
      ...grupo,
      fatorCorrecaoAplicado: Math.round(fatorCorrecaoAplicado),
      notaCorrigida: roundTo(notaCorrigida, DOIS),
      correcaoAplicada: roundTo(correcaoAplicada, DOIS)
    };
  });

  const somaNotaCorrigida = totalizadoresComNotasCorrigidas.reduce(
    (accumulator: number, grupo: any) => accumulator + (grupo.notaCorrigida || 0),
    0
  );

  // const mediaFinal = somaPesoGrupos > 0 ? ((somaNotaCorrigida / somaPesoGrupos) * CEM) / DEZ : 0;

  let mediaAtual = 0;
  let mediaAtualFonte = "respostas";

  if (medias.length > 0) {
    const somaMedias = medias.reduce(
      (accumulator: number, media: any) => accumulator + (Number.parseFloat(media.nota) || 0),
      0
    );
    mediaAtual = somaMedias / medias.length;
    mediaAtualFonte = "medias";
  } else {
    let somaRespostas = 0;
    let contadorRespostas = 0;

    respostasEnriquecidas.forEach((resposta: any) => {
      resposta.perguntas?.forEach((perguntaResposta: any) => {
        const valor = Number.parseFloat(perguntaResposta.resposta?.valor);
        if (!Number.isNaN(valor)) {
          somaRespostas += valor;
          contadorRespostas += 1;
        }
      });
    });

    mediaAtual = contadorRespostas > 0 ? somaRespostas / contadorRespostas : 0;
  }

  return {
    totalizadoresFinais: totalizadoresComNotasCorrigidas.map((grupo: any) => ({
      grupoId: grupo.grupoId,
      grupoNome: grupo.grupoNome,
      grupoPeso: grupo.grupoPeso,
      notaAA: grupo.notaAA,
      atingimentoAA: grupo.atingimentoAA,
      notaSegundaEntrevista: grupo.notaSegundaEntrevista,
      somaNotaSegundaEntrevista: roundTo(somaNotaSegundaEntrevista, DOIS),
      atingimentoSegundaEntrevista: grupo.atingimentoSegundaEntrevista,
      somaPesoGrupos: roundTo(somaPesoGrupos, DOIS),
      fatorCorrecao: `${roundTo(fatorCorrecao, DOIS)}%`,
      fatorCorrecaoAplicado: grupo.fatorCorrecaoAplicado,
      somaFatorCorrecaoAplicado: roundTo((fatorCorrecao / CEM) * somaPesoGrupos, DOIS),
      notaCorrigida: grupo.notaCorrigida,
      somaNotaCorrigida: roundTo(somaNotaCorrigida, DOIS),
      correcaoAplicada: `${grupo.correcaoAplicada}%`
    })),
    mediaAA: roundTo(mediaAtingimentoRespostas, DOIS),
    mediaAvaliadores: roundTo(mediaAtual, DOIS),
    mediaAtual: roundTo(mediaAtingimentoRespostas, DOIS),
    mediaAtualFonte
  };
};

const buscarDadosExportacao = async (parameters: ProcessarPlanilhaParameters) => {
  const { licenciadaId, empresaId, questionarioId, empresaDesejadaId } = parameters;

  const [snapPerguntas, snapGrupos, snapEmpresas, snapRespostas, snapMedias, snapColaboradores] = await Promise.all([
    getDocs(collection(firestore, `licenciadas/${licenciadaId}/empresas/${empresaId}/perguntas`)),
    getDocs(collection(firestore, `licenciadas/${licenciadaId}/empresas/${empresaId}/gruposDiagnostico`)),
    getDocs(collection(firestore, `licenciadas/${licenciadaId}/empresas`)),
    getDocs(collection(firestore, `licenciadas/${licenciadaId}/empresas/${empresaId}/respostas`)),
    getDocs(collection(firestore, `licenciadas/${licenciadaId}/empresas/${empresaId}/medias`)),
    getDocs(collection(firestore, `licenciadas/${licenciadaId}/empresas/${empresaId}/colaboradores`))
  ]);

  const perguntas = snapPerguntas.docs.map((document_) => ({ id: document_.id, ...document_.data() }));
  const grupos = snapGrupos.docs.map((document_) => ({ id: document_.id, ...document_.data() }));
  const empresas = snapEmpresas.docs.map((document_) => ({ id: document_.id, ...document_.data() }));
  const colaboradores = snapColaboradores.docs.map((document_) => ({ id: document_.id, ...document_.data() }));
  let respostas = snapRespostas.docs.map((document_) => ({ id: document_.id, ...document_.data() }));
  const medias = snapMedias.docs.map((document_) => ({ id: document_.id, ...document_.data() }));

  if (questionarioId) {
    respostas = respostas.filter((r: any) => r.idQuestionario === questionarioId);
  }

  const dadosMedias: any[] = [];
  medias.sort((a: any, b: any) => (a.grupo > b.grupo ? 1 : -1));
  medias.sort((a: any, b: any) => (a.idResposta > b.idResposta ? 1 : -1));

  for (const media of medias) {
    if (empresaDesejadaId && (media as any).idEmpresaNota !== empresaDesejadaId) continue;

    const pergunta = perguntas.find((p: any) => p.id === (media as any).idResposta);
    const grupo = grupos.find((g: any) => g.id === (media as any).grupo);
    const empresa = empresas.find((e: any) => e.id === (media as any).idEmpresaNota);
    const colaborador = colaboradores.find((c: any) => c.id === (media as any).respondente);

    if (pergunta && grupo && empresa) {
      dadosMedias.push({
        empresa: (empresa as any)?.nomeFantasia || (empresa as any)?.razaoSocial || empresa.id,
        respondente: (media as any).respondente,
        nomeColaborador: (colaborador as any)?.nome || (colaborador as any)?.email || "N/A",
        grupo: (grupo as any)?.nome,
        pergunta:
          (pergunta as any)?.descricao
            ?.replace(/(\n\r)/g, " ")
            .replaceAll(/\s+/g, " ")
            .trim() || "N/A",
        nota: (media as any).nota,
        pesoCalculado: (((media as any).nota / DEZ) * ((pergunta as any)?.peso || 0)).toFixed(DOIS)
      });
    }
  }

  const dadosRespostas: any[] = [];
  const respostasFiltradas = respostas.filter((r: any) => {
    if (empresaDesejadaId && r.idEmpresa !== empresaDesejadaId) return false;
    if (r.pausado === true) return false;
    return true;
  });

  for (const documento of respostasFiltradas) {
    if ((documento as any).perguntas) {
      (documento as any).perguntas.forEach((pergunta: any) => {
        const grupo = grupos.find((g: any) => g.id === pergunta.grupo);
        const empresa = empresas.find((e: any) => e.id === (documento as any).idEmpresa);
        const descPergunta = perguntas.find((p: any) => p.id === pergunta.id);
        const colaborador = colaboradores.find((c: any) => c.id === (documento as any).idRespondente);

        if (grupo && empresa && descPergunta) {
          dadosRespostas.push({
            empresa: (empresa as any)?.nomeFantasia || (empresa as any)?.razaoSocial || empresa.id,
            respondente: (documento as any)?.idRespondente,
            nomeColaborador: (colaborador as any)?.nome || (colaborador as any)?.email || "N/A",
            grupo: (grupo as any)?.nome,
            pergunta:
              (descPergunta as any)?.descricao
                ?.replace(/(\n\r)/g, " ")
                .replaceAll(/\s+/g, " ")
                .trim() || "N/A",
            valor: pergunta.resposta?.valor || 0,
            pesoCalculado: ((pergunta.resposta?.valor / DEZ) * ((descPergunta as any)?.peso || 0))
              .toFixed(DOIS)
              .replace(".", ",")
          });
        }
      });
    }
  }

  return { dadosMedias, dadosRespostas };
};

export const useProcessarPlanilha = () => {
  const processarPlanilhaEntrevista = async (parameters: ProcessarPlanilhaParameters): Promise<ConferenciaResult> => {
    try {
      const { perguntas, grupos, empresas, respostas, medias } = await fetchFirestoreData(parameters);
      const respostasEnriquecidas = enriquecerRespostas(respostas, perguntas, grupos);
      const { totalizadores: totalizadoresRespostas, mediaPercentuaisAtingimento: mediaAtingimentoRespostas } =
        calcularTotalizadores(respostasEnriquecidas, grupos, "respostas");
      const { totalizadores: totalizadoresMedias } = calcularTotalizadores(medias as any[], grupos, "medias");
      gerarLogCSV(respostasEnriquecidas, medias, empresas, parameters.empresaDesejadaId);
      const { totalizadoresFinais, mediaAA, mediaAvaliadores, mediaAtual, mediaAtualFonte } = calcularValoresFinais(
        totalizadoresRespostas,
        totalizadoresMedias,
        medias,
        respostasEnriquecidas,
        mediaAtingimentoRespostas
      );
      const { dadosMedias, dadosRespostas } = await buscarDadosExportacao(parameters);
      let dadosEmpresa = null;
      if (parameters.empresaDesejadaId) {
        const empresa = empresas.find((empresa: any) => empresa.id === parameters.empresaDesejadaId);
        if (empresa) {
          dadosEmpresa = {
            empresaId: empresa.id,
            empresaNome: (empresa as any)?.nomeFantasia || (empresa as any)?.razaoSocial,
            empresaRazaoSocial: (empresa as any)?.razaoSocial
          };
        }
      }

      return {
        empresa: dadosEmpresa,
        totalRespostas: respostasEnriquecidas.length,
        totalPerguntas: perguntas.length,
        totalGrupos: grupos.length,
        totalMedias: medias.length,
        mediaAtual,
        mediaAtualFonte,
        mediaAA: `${mediaAA}%`,
        mediaAvaliadores: `${mediaAvaliadores}%`,
        totalizadoresPorGrupo: totalizadoresFinais,
        respostas: respostasEnriquecidas,
        dadosMedias,
        dadosRespostas
      };
    } catch (error) {
      console.error("Erro no processamento:", error);
      throw error;
    }
  };

  return { processarPlanilhaEntrevista };
};
